import type { PaymentViewModel } from "@lumora/core";
import { useViewModel } from "@lumora/ui";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";

export interface PaymentFormProps {
  viewModel: PaymentViewModel;
  /** Where Stripe redirects after a successful confirmation. */
  returnUrl: string;
}

/**
 * PaymentForm renders the Stripe Payment Element and confirms payment on submit.
 * All observable state (processing/error/success) lives in PaymentViewModel; the
 * View only performs the client-only Stripe SDK calls and hands the VM a thunk.
 */
export function PaymentForm({ viewModel, returnUrl }: PaymentFormProps) {
  const state = useViewModel(viewModel);
  const stripe = useStripe();
  const elements = useElements();

  const ready = Boolean(stripe && elements);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    void viewModel.confirm(() =>
      stripe.confirmPayment({ elements, confirmParams: { return_url: returnUrl } }),
    );
  }

  if (state.status === "succeeded") {
    return (
      <p className="portal-notice" data-lumora-payment-success>
        Payment confirmed — thank you!
      </p>
    );
  }

  return (
    <form className="portal-form" onSubmit={handleSubmit} data-lumora-view="payment">
      <h3>Set up autopay</h3>
      {state.error ? (
        <p data-lumora-error className="portal-error">
          {state.error}
        </p>
      ) : null}
      <PaymentElement />
      <button
        type="submit"
        className="portal-btn"
        disabled={!ready || state.status === "processing"}
      >
        {state.status === "processing" ? "Processing…" : "Pay & subscribe"}
      </button>
    </form>
  );
}
